// Copyright (c) 2024 Readymadeui
// Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
// The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

import React, { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {localizeKey, localizeArt} from './../localize.js';
import axiosInstance from './../axiosInstance.js';
import MyUserContext from './../myUserContext.js';
import Collapsible from './Collapsible';
import LinkInput from './LinkInput';
import TextUserPair from './TextUserPair';
import CopyTextButton from './CopyTextButton';
import DotSpinner from './DotSpinner';
import Tabs from './Tabs';
import ArtPage from './ArtPage';
import getIconForLink from '../iconForLink.js';
import { Link } from 'react-router-dom';
import PlatformRatings from './PlatformRatings';
import ArtTabs from './ArtTabs';
import 'delayed-scroll-restoration-polyfill';
import ArtThumbnail from './ArtThumbnail.js';
import { SegmentedControl, TextInput, ActionIcon, Paper, Text, Tooltip, Avatar } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { formatYear } from './../utils.js'; // Import the global function

// Move cache outside the component to persist across renders
const cache = new Map();

const AddEvent = ({repeatedEnter = 0}) => {
  const myUser = useContext(MyUserContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [art, setArt] = useState(null);
  const [addedEvents, setAddedEvents] = useState([]);
  // const [successSign, setSuccessSign] = useState(null); // like, todo, finished

  const [searching, setSearching] = useState(false);
  //const [foundArts, setFoundArts] = useState([]);

  // const [foundArtsByType, setFoundArtsByType] = useState(new Map());

  const [foundMovies, setFoundMovies] = useState(null);
  const [foundBooks, setFoundBooks] = useState(null);
  const [foundGames, setFoundGames] = useState(null);
  const [foundUsers, setFoundUsers] = useState(null);
  const [userOnlySearch, setUserOnlySearch] = useState(false);

  const userSearchSeqRef = useRef(0);

  const searchInputRef = useRef(null);
  const [renderCount, setRenderCount] = useState(0);
  const prevRepeatedEnterRef = useRef(repeatedEnter);
  useEffect(() => {
    if (repeatedEnter !== prevRepeatedEnterRef.current) {
      prevRepeatedEnterRef.current = repeatedEnter;
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }
  });

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const searchTerm = queryParams.get('term');
    const limit = queryParams.get('limit') || 20;
    // const author = queryParams.get('author');
    if (searchTerm) {
    // if (searchTerm || author) {
      // if (author) {
      //   searchTerm = "author:"+author;
      // }
      if (searchTerm.startsWith('@')) {
        setUserOnlySearch(true);
        handleSearchUsers(searchTerm.slice(1));
      } else {
        handleSearchArt(searchTerm, "movie", limit);
        handleSearchArt(searchTerm, "book", limit);
        handleSearchArt(searchTerm, "videoGame", limit);
        handleSearchUsers(searchTerm);
      }
      searchInputRef.current.value = searchTerm;
    }
  }, []);

  useEffect(() => {
    // Enable scroll restoration
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  const [enterPressed, setEnterPressed] = useState(false); // to prevent suggest flickering when response for old request comes

  const handleSearchUsers = (term, limit = 50) => {
    if (term === "") {
      return;
    }
    setFoundUsers(null);
    const mySeq = ++userSearchSeqRef.current;
    axiosInstance
      .get(`/searchUsers?userName=${encodeURIComponent(term)}&limit=${limit}&allUsers=true`)
      .then((response) => {
        if (mySeq !== userSearchSeqRef.current) return;
        setFoundUsers(response.data || []);
      })
      .catch((error) => {
        if (mySeq !== userSearchSeqRef.current) return;
        console.log(error);
        setFoundUsers([]);
      });
  };

  const typingSearchClearTimeoutRef = useRef(null);
  const [searchSuggest, setSearchSuggest] = useState(null);
  const [searchTerm, setSearchTerm] = useState(null);
  const [searchHistory, setSearchHistory] = useState([]);
  const [showSearchHistory, setShowSearchHistory] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const userInitiatedFocusRef = useRef(false);
  const swipeTouchXRef = useRef(0);
  const swipeTouchYRef = useRef(0);
  const swipeActiveRef = useRef(false);
  const handleSearchSuggest = (term) => {
    axiosInstance
    .get(`/arts/searchSuggest?term=${term}`)
    .then((response) => {
      // console.log("term: " + term + ", searchInputRef.current.value: " + searchInputRef.current.value);
      if (searchInputRef.current.value !== term) { // late response // TODO: move into the component's main code
        return;
      }
      setSearchSuggest(response.data);
      setHighlightedIndex(-1);
    })
    .catch((error) => {
      console.log(error);
    })
  };
  const handleSearchHistory = () => {
    axiosInstance
    .get(`/arts/searchHistory`)
    .then((response) => {
      setSearchHistory(response.data || []);
    })
    .catch((error) => {
      console.log(error);
    })
  };

  const handleSearchArt = (term, artType, limit=25) => {
    if (term === "") {
      return;
    }

    const cacheKey = `${term}-${artType}`;
    const cachedResult = cache.get(cacheKey);

    // Remove stale data from cache
    for (const [key, value] of cache.entries()) {
      if (Date.now() - value.timestamp >= 10 * 60 * 1000) { // 10 minutes
        cache.delete(key);
      }
    }

    if (cachedResult && Date.now() - cachedResult.timestamp < 10 * 60 * 1000) {
      const data = cachedResult.data;
      if (artType === "movie") {
        setFoundMovies(data);
      } else if (artType === "book") {
        setFoundBooks(data);
      } else {
        setFoundGames(data);
      }
      return;
    }

    setSearching(true);
    setFoundMovies(null);
    setFoundGames(null);
    setFoundBooks(null);
    setArt(null);


    axiosInstance
      .get(`/arts/search?term=${term}&artType=${artType}&limit=${limit}`)
      .then((response) => {
        setSearching(false);
        setSearchSuggest(null);

        if (addedEvents.length > 0) {
          return;
        }

        const data = response.data;
        cache.set(cacheKey, { data, timestamp: Date.now() });

        if (artType === "movie") {
          setFoundMovies(data);
        } else if (artType === "book") {
          setFoundBooks(data);
        } else {
          setFoundGames(data);
        }
      })
      .catch((error) => {
        console.error(error);
        setSearching(false);
        if (error.response.status === 400) {
          alert(error.response.data);
        }
      });
  };

  const isMobileDeviceExceptAndroid = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    return /iphone|ipad|ipod|windows phone|blackberry/i.test(userAgent);
  };

  const isFirefox = () => {
    return navigator.userAgent.toLowerCase().includes('firefox');
  };

  const handleArtSubmit = (link) => {
    axiosInstance
    .post(`/arts`, { wikiArticleLink: link })
    .then((response) => {
      // setAddedEvents(prevObjects => [response.data, ...prevObjects]); // not sure
      // setFoundMovies(null);
      // setFoundGames(null);
      // setFoundBooks(null);
      // setActiveTab('movie');
      //setArt(response.data);
      navigate("/arts/"+response.data.id);
      // setLinkInput(""); // not sure
      // setSuccessSign(eventType);
    })
    .catch((error) => {
      console.error(error);
      // setSuccessSign(null);
      // setLinkInputErr(true);
    });
  };

  // Determine the tab to show based on found results.
  // Expects non-null results and exactly one tab with results.
  const determineTabBasedOnResults = () => {
    let res;
    if (foundMovies.length > 0) {
      res = 'movie';
    } else if (foundBooks.length > 0) {
      res = 'book';
    } else if (foundGames.length > 0) {
      res = 'videoGame';
    } else if (foundUsers && foundUsers.length > 0) {
      res = 'users';
    }
    return res;
  };

  const getActiveTabFromURL = () => {
    const p = new URLSearchParams(location.search).get('tab');
    return (p === 'book' || p === 'videoGame' || p === 'users') ? p : 'movie';
  };
  const [activeTab, setActiveTabState] = useState(getActiveTabFromURL);
  const setActiveTab = (tab) => {
    setActiveTabState(tab);
    const params = new URLSearchParams(location.search);
    params.set('tab', tab);
    navigate(`/addEvent?${params.toString()}`, { replace: true });
  };

  // Update activeTab dynamically when results are fetched
  useEffect(() => {
    if (!foundMovies || !foundBooks || !foundGames || !foundUsers) { // too early to determine tab
      return;
    }
    let nonZeroTabs = 0;
    if (foundMovies.length > 0) nonZeroTabs++;
    if (foundBooks.length > 0) nonZeroTabs++;
    if (foundGames.length > 0) nonZeroTabs++;
    if (foundUsers.length > 0) nonZeroTabs++;
    if (nonZeroTabs == 1) {
      setActiveTab(determineTabBasedOnResults());
    }
  }, [foundMovies, foundBooks, foundGames, foundUsers]);

  const foundArtContainerByTab = (tab) => {
    if (tab === "movie") {
      return foundMovies;
    } else if (tab === "book") {
      return foundBooks;
    } else if (tab === "videoGame") {
      return foundGames;
    } else if (tab === "users") {
      return null; // rendered separately
    } else {
      alert("Unexpected tab");
      return foundMovies;
    }
  }

  const eventTypeStr = (i) => {
    const m = {
      0: "none",
      1: "finished",
      2: "todo",
      3: "like"
    };
    return m[i];
  }

  const getTrendingCategoryFromLocation = () => {
    const p = new URLSearchParams(location.search).get('cat');
    return (p === 'book' || p === 'videoGame') ? p : 'movie';
  };
  const trendingCategory = getTrendingCategoryFromLocation();
  const trendingCategoryRef = useRef(trendingCategory);
  trendingCategoryRef.current = trendingCategory;

  const setTrendingCategory = (cat) => {
    trendingCategoryRef.current = cat;
    const params = new URLSearchParams(location.search);
    params.set('cat', cat);
    navigate(`/addEvent?${params.toString()}`, { replace: true });
  };

  // const [showTrending, setShowTrending] = useState(true); // if false, recommendations are shown
  const [trendingMovies, setTrendingMovies] = useState(null);
  const [trendingMoviesUpdatedTime, setTrendingMoviesUpdatedTime] = useState(null);
  const [selectedTrendingId, setSelectedTrendingId] = useState(null);
  const [trendingDetails, setTrendingDetails] = useState({});

  const prevResetRef = useRef(repeatedEnter);
  useEffect(() => {
    if (repeatedEnter !== prevResetRef.current) {
      prevResetRef.current = repeatedEnter;
      setFoundMovies(null);
      setFoundBooks(null);
      setFoundGames(null);
      setFoundUsers(null);
      setUserOnlySearch(false);
      setSearchSuggest(null);
      setSelectedTrendingId(null);
      if (searchInputRef.current) {
        searchInputRef.current.value = '';
      }
      if (location.search) {
        navigate('/addEvent', { replace: true });
      }
    }
  }, [repeatedEnter]);

  const scrollDetailIntoView = (el) => {
    if (!el) return;
    requestAnimationFrame(() => {
      const rect = el.getBoundingClientRect();
      if (rect.bottom > window.innerHeight) {
        window.scrollBy({ top: rect.bottom - window.innerHeight + 60, behavior: 'smooth' });
      }
    });
  };

  // Batch-fetch descriptions when trending list loads
  useEffect(() => {
    if (!trendingMovies || trendingMovies.length === 0) return;
    const ids = trendingMovies.map(a => a.id).filter(id => !trendingDetails[id]);
    if (ids.length === 0) return;
    axiosInstance.post('/arts/descriptions', { ids })
      .then((response) => {
        setTrendingDetails(prev => ({ ...prev, ...response.data }));
      })
      .catch((error) => console.error('Failed to fetch art descriptions:', error));
  }, [trendingMovies]);

  const handleSelectTrending = (artId) => {
    setSelectedTrendingId(selectedTrendingId === artId ? null : artId);
  };
  const fetchTrendingMovies = () => {
    const queryParams = new URLSearchParams(location.search);
    const popMostRecent = queryParams.get('popMostRecent');

    return axiosInstance
      .get(`/trendingMovies${popMostRecent ? `?popMostRecent=${popMostRecent}` : ''}`)
      .then((response) => {
        setTrendingMovies(response.data.arts);
        setTrendingMoviesUpdatedTime(response.data.updatedTime);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    (async () => {
      await fetchTrendingMovies();
      handleSearchHistory();
    })();
  }, []);

  const language = myUser ? myUser.myUserSettings.lang : 'ru';
  const showSuggestList = !enterPressed && searchSuggest && searchSuggest.length > 0;
  const showHistoryList = showSearchHistory && (!searchTerm || searchTerm.length === 0) && searchHistory && searchHistory.length > 0;
  const categoryLabels = {
    movie: language === "ru" ? "Фильмы" : "Movies",
    book: language === "ru" ? "Книги" : "Books",
    videoGame: language === "ru" ? "Игры" : "Video Games"
  };
  const categoryOrder = ["movie", "book", "videoGame"];
  const categoryEnum = {
    movie: 1,
    videoGame: 2,
    book: 3
  };
  const filterByCategory = (arts, category) => {
    if (!arts) {
      return [];
    }
    return arts.filter((item) => item.category === categoryEnum[category]);
  };

  const formatUpdatedAgo = (updatedTime) => {
    if (!updatedTime) {
      return "";
    }
    const now = Date.now();
    const updatedAt = new Date(updatedTime).getTime();
    let diffMinutes = Math.max(0, Math.floor((now - updatedAt) / 60000));
    if (diffMinutes < 60) {
      const unit = language === "ru" ? "мин" : (diffMinutes === 1 ? "minute" : "minutes");
      return language === "ru"
        ? `Обновлено ${diffMinutes} ${unit} назад`
        : `Updated ${diffMinutes} ${unit} ago`;
    }
    const diffHoursTotal = Math.floor(diffMinutes / 60);
    const ruPluralHour = (count) => {
      const mod10 = count % 10;
      const mod100 = count % 100;
      if (mod10 === 1 && mod100 !== 11) {
        return "час";
      }
      if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
        return "часа";
      }
      return "часов";
    };
    if (diffHoursTotal < 24) {
      const unit = language === "ru" ? ruPluralHour(diffHoursTotal) : (diffHoursTotal === 1 ? "hour" : "hours");
      return language === "ru"
        ? `Обновлено ${diffHoursTotal} ${unit} назад`
        : `Updated ${diffHoursTotal} ${unit} ago`;
    }
    const diffDays = Math.floor(diffHoursTotal / 24);
    const diffHoursRemainder = diffHoursTotal % 24;
    const dayUnit = language === "ru" ? "дн" : (diffDays === 1 ? "day" : "days");
    const hourUnit = language === "ru" ? ruPluralHour(diffHoursRemainder) : (diffHoursRemainder === 1 ? "hour" : "hours");
    if (diffHoursRemainder === 0) {
      return language === "ru"
        ? `Обновлено ${diffDays} ${dayUnit} назад`
        : `Updated ${diffDays} ${dayUnit} ago`;
    }
    return language === "ru"
      ? `Обновлено ${diffDays} ${dayUnit} ${diffHoursRemainder} ${hourUnit} назад`
      : `Updated ${diffDays} ${dayUnit} ${diffHoursRemainder} ${hourUnit} ago`;
  };

  const performSearch = (term) => {
    const nextTerm = term.trim();
    if (nextTerm === "") {
      return;
    }

    if (searchInputRef.current) {
      searchInputRef.current.blur();
    }
    setEnterPressed(true);
    setSearchSuggest(null);
    setShowSearchHistory(false);
    setSearchTerm(nextTerm);
    if (searchInputRef.current) {
      searchInputRef.current.value = nextTerm;
    }

    if (nextTerm.startsWith("https://")) {
      handleArtSubmit(nextTerm);
      return;
    }

    if (nextTerm.startsWith("@")) {
      setUserOnlySearch(true);
      setFoundMovies(null);
      setFoundBooks(null);
      setFoundGames(null);
      handleSearchUsers(nextTerm.slice(1));
      setActiveTab('users');
      navigate(`/addEvent?term=${encodeURIComponent(nextTerm)}&tab=users`, { replace: true });
      return;
    }

    setUserOnlySearch(false);
    handleSearchArt(nextTerm, "movie");
    handleSearchArt(nextTerm, "book");
    handleSearchArt(nextTerm, "videoGame");
    handleSearchUsers(nextTerm);

    navigate(`/addEvent?term=${encodeURIComponent(nextTerm)}&tab=${activeTab}`, { replace: true });
  };

  return (
    <div className="w-full px-0 md:px-1 z-1">
      <div>
        <div className="mb-1 mt-1 max-w-md mx-auto">
          <TextInput
            ref={searchInputRef}
            size="lg"
            radius="xl"
            placeholder={language === "ru" ? "Поиск по фильмам, книгам и играм..." : "Search movies, books, or games..."}
            value={searchTerm}
            onKeyDown={(e) => {
              const items = showSuggestList ? searchSuggest : (showHistoryList ? searchHistory : null);
              const itemCount = items ? items.length : 0;

              if (e.key === 'ArrowDown' && itemCount > 0) {
                e.preventDefault();
                setHighlightedIndex(prev => prev < itemCount - 1 ? prev + 1 : 0);
                return;
              }
              if (e.key === 'ArrowUp' && itemCount > 0) {
                e.preventDefault();
                setHighlightedIndex(prev => prev > 0 ? prev - 1 : itemCount - 1);
                return;
              }
              if (e.key === 'Escape') {
                setSearchSuggest(null);
                setShowSearchHistory(false);
                setHighlightedIndex(-1);
                return;
              }
              if (e.key === 'Enter') {
                if (highlightedIndex >= 0 && items && items[highlightedIndex]) {
                  const selected = showSuggestList ? items[highlightedIndex].text : items[highlightedIndex];
                  performSearch(selected);
                } else {
                  performSearch(e.target.value);
                }
                setHighlightedIndex(-1);
                return;
              }
              setSearchSuggest(null);
              setShowSearchHistory(false);
              setHighlightedIndex(-1);
            }}
            onChange={(e) => {
              setEnterPressed(false);
              setShowSearchHistory(false);
              setSearchTerm(e.target.value);
              clearTimeout(typingSearchClearTimeoutRef.current);
              typingSearchClearTimeoutRef.current = setTimeout(() => {
                handleSearchSuggest(e.target.value);
              }, 90);
            }}
            onMouseDown={() => { userInitiatedFocusRef.current = true; }}
            onTouchStart={() => { userInitiatedFocusRef.current = true; }}
            onPointerDown={() => { userInitiatedFocusRef.current = true; }}
            onFocus={() => {
              if (userInitiatedFocusRef.current) {
                setShowSearchHistory(true);
              }
              userInitiatedFocusRef.current = false;
            }}
            onBlur={() => {
              setTimeout(() => { setShowSearchHistory(false); }, 150);
            }}
            leftSection={
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 192.904 192.904" width="16px" style={{ fill: "var(--mantine-color-dimmed)" }}>
                <path d="m190.707 180.101-47.078-47.077c11.702-14.072 18.752-32.142 18.752-51.831C162.381 36.423 125.959 0 81.191 0 36.422 0 0 36.423 0 81.193c0 44.767 36.422 81.187 81.191 81.187 19.688 0 37.759-7.049 51.831-18.751l47.079 47.078a7.474 7.474 0 0 0 5.303 2.197 7.498 7.498 0 0 0 5.303-12.803zM15 81.193C15 44.694 44.693 15 81.191 15c36.497 0 66.189 29.694 66.189 66.193 0 36.496-29.692 66.187-66.189 66.187C44.693 147.38 15 117.689 15 81.193z" />
              </svg>
            }
            rightSection={
              (isMobileDeviceExceptAndroid() || isFirefox()) && searchTerm ? (
                <ActionIcon variant="subtle" color="gray" size="sm" onClick={() => {
                  setSearchTerm('');
                  searchInputRef.current.value = '';
                  searchInputRef.current.focus();
                }}>
                  &times;
                </ActionIcon>
              ) : null
            }
          />
        </div>
        <div className="flex justify-center">
          <div className="absolute z-50 rounded-md max-w-md mx-auto w-full" style={{ background: "var(--mantine-color-body)", border: (showSuggestList || showHistoryList) ? "1px solid var(--mantine-color-default-border)" : "none" }}>
            {showSuggestList && searchSuggest.map((suggest, index) => (
              <div
                key={suggest.imageUrl}
                className={"p-2 cursor-pointer flex items-center" + (index===0 ? " rounded-t-md" : "") + (index === searchSuggest.length-1 ? " rounded-b-md" : "")}
                style={{ background: index === highlightedIndex ? "var(--mantine-color-default-hover)" : "var(--mantine-color-body)" }}
                onMouseOver={() => setHighlightedIndex(index)}
                onMouseOut={() => setHighlightedIndex(-1)}
                onClick={() => {
                  performSearch(suggest.text);
                }}
              >
                {suggest.text}
              </div>
            ))}
            {showHistoryList && searchHistory.map((term, index) => (
              <div
                key={`${term}-${index}`}
                className={"p-2 cursor-pointer flex items-center" + (index===0 ? " rounded-t-md" : "") + (index === searchHistory.length-1 ? " rounded-b-md" : "")}
                style={{ background: index === highlightedIndex ? "var(--mantine-color-default-hover)" : "var(--mantine-color-body)" }}
                onMouseOver={() => setHighlightedIndex(index)}
                onMouseOut={() => setHighlightedIndex(-1)}
                onClick={() => {
                  performSearch(term);
                }}
              >
                {term}
              </div>
            ))}
          </div>
        </div>
      </div>

      {
      (foundMovies || foundBooks || foundGames || foundUsers) &&
      <div className="my-2">
        <ArtTabs
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          language={language}
          movies={userOnlySearch ? undefined : foundMovies}
          books={userOnlySearch ? undefined : foundBooks}
          games={userOnlySearch ? undefined : foundGames}
          users={foundUsers}
                  />
      </div>
      }

      {
        searching && 
        <div className="flex justify-center">
          <DotSpinner />
        </div>
      }

      {activeTab === "users" && foundUsers && (
        <div className="mt-4 flex justify-center">
          <Paper shadow="xs" radius="md" className="w-full" style={{ maxWidth: 480, padding: 8 }}>
            {foundUsers.length === 0 ? (
              <Text size="sm" c="dimmed" ta="center" py="md">
                {language === 'ru' ? 'Пользователи не найдены' : 'No users found'}
              </Text>
            ) : (
              foundUsers.map((u) => (
                <Link key={u.id} to={`/users/${u.id}/likes`} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <div
                    className="flex items-center gap-3 p-2 rounded-md cursor-pointer"
                    style={{ transition: 'background 120ms' }}
                    onMouseOver={(e) => { e.currentTarget.style.background = 'var(--mantine-color-default-hover)'; }}
                    onMouseOut={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <Avatar src={u.avatarUrl} size="md" radius="xl" />
                    <Text size="sm" fw={500}>{u.userName}</Text>
                  </div>
                </Link>
              ))
            )}
          </Paper>
        </div>
      )}

      <div className="flex justify-center">
        <div id={activeTab} className="grid grid-cols-4 gap-3">
          {foundArtContainerByTab(activeTab) && foundArtContainerByTab(activeTab).map((foundArt, index) => (
            <div id={foundArt.pageUrl} className="hover:shadow-lg transition-shadow duration-200">
              <div>
                <div id={foundArt.pageUrl+"0"} className="flex justify-center">
                  <Link to={foundArt.sourceArt ? "/arts/"+foundArt.sourceArt.id : "/artByLink?link="+foundArt.pageUrl}>
                    <ArtThumbnail art={foundArt.sourceArt ? foundArt.sourceArt : {thumbnailUrl: foundArt.thumbUrl}} />
                  </Link>
                </div>
                <div className="flex justify-center items-center text-center w-full h-auto text-sm font-medium mt-2">
                  {( (language === "en" || !foundArt.ruName) ? foundArt.enName : foundArt.ruName) + formatYear(foundArt.year, language)}
                </div>
                {activeTab === "book" && foundArt.author && (
                  <div className="flex justify-center items-center text-center w-full h-auto text-xs mt-1" style={{ color: "var(--mantine-color-dimmed)" }}>
                    {foundArt.author}
                  </div>
                )}
                <div className="flex justify-center mt-1">
                {
                  foundArt.sourceArt ?
                    <PlatformRatings art={foundArt.sourceArt} />
                  :
                    foundArt.ratings &&
                      <PlatformRatings art={foundArt.ratings} />
                }
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {trendingMovies && !(foundMovies || foundBooks || foundGames || foundUsers) && !searchTerm && (
        <div className="mt-4">
          <div className="flex justify-center items-center gap-1 mb-2">
            <SegmentedControl
              value={trendingCategory}
              onChange={setTrendingCategory}
              data={categoryOrder.map(c => ({ label: categoryLabels[c], value: c }))}
              size="sm"
              withItemsBorders={false}
              styles={{ root: { border: 'none' }, control: { border: 'none' } }}
            />
            <Tooltip
              label={
                trendingCategory === 'movie'
                  ? (language === "ru"
                    ? "Новые фильмы. Те, которые сейчас активнее всего получают оценки на IMDb и Кинопоиске — выше."
                    : "New movies. Those currently most actively reviewed on IMDb are at the top.")
                  : trendingCategory === 'book'
                  ? (language === "ru" ? "Новые книги." : "New books.")
                  : (language === "ru" ? "Новые игры." : "New games.")
              }
              multiline
              w={250}
              withArrow
              events={{ hover: true, focus: true, touch: true }}
            >
              <ActionIcon variant="subtle" size="sm" color="dimmed">
                <IconInfoCircle size={18} />
              </ActionIcon>
            </Tooltip>
          </div>
          <div className="flex justify-center text-xs text-center mb-2" style={{ color: "var(--mantine-color-dimmed)" }}>
            {formatUpdatedAgo(trendingMoviesUpdatedTime)}
          </div>
          <div
            onTouchStart={(e) => {
              swipeTouchXRef.current = e.touches[0].clientX;
              swipeTouchYRef.current = e.touches[0].clientY;
              swipeActiveRef.current = false;
            }}
            onTouchMove={(e) => {
              const dx = e.touches[0].clientX - swipeTouchXRef.current;
              const dy = e.touches[0].clientY - swipeTouchYRef.current;
              if (!swipeActiveRef.current && Math.abs(dx) > 20 && Math.abs(dx) > Math.abs(dy) * 1.2) {
                swipeActiveRef.current = true;
              }
            }}
            onTouchEnd={(e) => {
              if (!swipeActiveRef.current) return;
              e.preventDefault();
              const dx = e.changedTouches[0].clientX - swipeTouchXRef.current;
              const idx = categoryOrder.indexOf(trendingCategoryRef.current);
              if (dx < -40 && idx < categoryOrder.length - 1) {
                setTrendingCategory(categoryOrder[idx + 1]);
              }
              if (dx > 40 && idx > 0) {
                setTrendingCategory(categoryOrder[idx - 1]);
              }
            }}
            style={{ minHeight: 100, touchAction: 'pan-y' }}
          >
            {(() => {
              const items = filterByCategory(trendingMovies, trendingCategory);
              if (items.length === 0) {
                return (
                  <div className="flex justify-center items-center py-12" style={{ color: "var(--mantine-color-dimmed)" }}>
                    {language === "ru" ? "Скоро появятся" : "Coming soon"}
                  </div>
                );
              }
              const cols = window.innerWidth >= 640 ? 6 : 4;
              const elements = [];
              for (let i = 0; i < items.length; i += cols) {
                const rowItems = items.slice(i, i + cols);
                const selectedInRow = selectedTrendingId && rowItems.some(a => a.id === selectedTrendingId);
                const selectedArtLite = selectedInRow ? rowItems.find(a => a.id === selectedTrendingId) : null;
                const detail = selectedTrendingId ? trendingDetails[selectedTrendingId] : null;

                elements.push(
                  <div key={`row-${i}`} className="grid grid-cols-4 sm:grid-cols-6 gap-1.5 mb-1.5">
                    {rowItems.map((art) => {
                      const localized = localizeArt(art, language);
                      return (
                        <div key={art.id} className="cursor-pointer" onClick={() => handleSelectTrending(art.id)}>
                          <ArtThumbnail art={art} />
                        </div>
                      );
                    })}
                  </div>
                );

                if (selectedInRow && selectedArtLite) {
                  const desc = detail || {};
                  const description = language === 'ru'
                    ? (desc.ruDescription || desc.description)
                    : (desc.description || desc.ruDescription);
                  const genres = selectedArtLite.genres || [];
                  const author = selectedArtLite.author || '';
                  elements.push(
                    <Paper key={`detail-${i}`} ref={scrollDetailIntoView} shadow="xs" radius="md" className="mb-2 p-3" style={{ backgroundColor: 'var(--app-surface-hover)' }}>
                      <div className="flex gap-3">
                        <Link to={`/arts/${selectedArtLite.id}`}>
                          <img src={selectedArtLite.thumbnailUrl} alt="" className="w-16 h-auto object-cover rounded-md" />
                        </Link>
                        <div className="flex-1 min-w-0">
                          <Link to={`/arts/${selectedArtLite.id}`}>
                            <Text size="sm" fw={600}>{localizeArt(selectedArtLite, language).name + formatYear(selectedArtLite.year, language)}</Text>
                          </Link>
                          {author && (
                            <Text size="xs" c="dimmed" className="mt-0.5">
                              {selectedArtLite.category === 1
                                ? (language === 'ru' ? 'Режиссёр' : 'Director')
                                : selectedArtLite.category === 2
                                  ? (language === 'ru' ? 'Разработчик' : 'Developer')
                                  : (language === 'ru' ? 'Автор' : 'Author')
                              }: {author}
                            </Text>
                          )}
                          {genres.length > 0 && (
                            <Text size="xs" c="dimmed" className="mt-0.5">
                              {genres.join(', ')}
                            </Text>
                          )}
                          <div className="mt-1">
                            <PlatformRatings art={selectedArtLite} center={false} />
                          </div>
                          {description ? (
                            <Text size="xs" className="mt-1" style={{ lineHeight: 1.4 }}>
                              {description}
                            </Text>
                          ) : detail && (
                            <Text size="xs" c="dimmed" className="mt-1" style={{ fontStyle: 'italic' }}>
                              {language === 'ru' ? 'Описание отсутствует' : 'No description available'}
                            </Text>
                          )}
                        </div>
                      </div>
                    </Paper>
                  );
                }
              }
              return elements;
            })()}
          </div>
        </div>
      )}

    </div>
  );
}

export default AddEvent;
